<!--
  @component DeployStreamBlock — the live, fleet-wide deploy feed.
  tier:   deployments block

  Newest-first stream of deploy events across every project (the same WS
  event stream the per-project pipeline renders, aggregated + humanised via
  home-util's feedEntries). Updates live as deploys happen.
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import { feedEntries, relTime, type FeedTone } from "../../home/home-util.js";
  import { scopeEvents } from "./fleet-util.js";
  import { useScopeIO } from "./ScopeIO.svelte";

  const identity = useIdentityIO();
  const provenance = useProvenanceIO();
  const scope = useScopeIO();

  const scopedEvents = $derived(scopeEvents(provenance.events, scope.projectIds));

  function nameOf(pid: string | null): string | null {
    return pid ? (identity.projects.find((p) => p.id === pid)?.name ?? null) : null;
  }

  const entries = $derived(feedEntries(scopedEvents, 18));

  const TONE_CLASS: Record<FeedTone, string> = {
    ok: "ok", info: "info", warn: "warn", error: "error",
  };
</script>

<section class="stream">
  <header class="head">
    <h2>Live deploys</h2>
    <span class="live-dot" aria-hidden="true"></span>
  </header>

  {#if entries.length === 0}
    <p class="empty">No deploy activity yet. It'll stream in here as you ship.</p>
  {:else}
    <ul class="feed">
      {#each entries as e (e.id)}
        <li class="row">
          <span class="tone {TONE_CLASS[e.tone]}" aria-hidden="true"></span>
          <span class="label">{e.label}</span>
          {#if nameOf(e.pid)}<span class="proj">{nameOf(e.pid)}</span>{/if}
          <span class="time">{relTime(e.atMs)}</span>
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .stream {
    display: flex; flex-direction: column; gap: var(--space-3);
    padding: var(--space-5);
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-xl, 18px);
    backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur);
    font-family: var(--type-font);
    min-height: 0;
  }
  .head { display: flex; align-items: center; gap: var(--space-2); }
  h2 { margin: 0; font-size: 15px; font-weight: 600; letter-spacing: -0.01em; color: var(--text-primary); }
  .live-dot {
    width: 7px; height: 7px; border-radius: 50%; background: var(--success);
    box-shadow: 0 0 0 3px color-mix(in oklab, var(--success) 22%, transparent);
    animation: blink 2.4s ease-in-out infinite;
  }
  @keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
  @media (prefers-reduced-motion: reduce) { .live-dot { animation: none; } }

  .empty { margin: 0; font-size: 13px; color: var(--text-tertiary); line-height: 1.5; }
  .feed { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; max-height: 360px; overflow-y: auto; scrollbar-width: thin; }
  .row {
    display: flex; align-items: center; gap: var(--space-2);
    padding: 8px 0; border-bottom: 0.5px solid var(--border-subtle);
    font-size: 12.5px;
  }
  .row:last-child { border-bottom: 0; }
  .tone { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; background: var(--text-tertiary); }
  .tone.ok { background: var(--success); }
  .tone.warn { background: var(--warning); }
  .tone.error { background: var(--error); }
  .tone.info { background: var(--accent, #5b8cff); }
  .label { color: var(--text-primary); white-space: nowrap; }
  .proj {
    flex: 1; min-width: 0; color: var(--text-tertiary); font-size: 11.5px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .time { margin-left: auto; color: var(--text-ghost); font-size: 11.5px; flex-shrink: 0; }
  .proj + .time { margin-left: var(--space-2); }
</style>
